import type { ReportFilterClause, ReportFilters, ReportFormat } from '@eis/core/reporting';

/**
 * The report document as the console edits it (`api/schemas/dataservice/report.schema.json`,
 * SDD-024 D-1…D-5). Typed here, validated by the service (D-1): the editor never decides whether a
 * document is valid, it only keeps it in the shape the schema names.
 */
export type ReportType = 'TABLE' | 'PIVOT' | 'CHART';
export type Aggregation = 'SUM' | 'COUNT' | 'COUNT_DISTINCT' | 'AVG' | 'MIN' | 'MAX';
export type Bucket = 'YEAR' | 'QUARTER' | 'MONTH' | 'DAY';
export type FieldType = 'STRING' | 'NUMBER' | 'DATE' | 'DATETIME' | 'BOOLEAN';

export type DatasetRef =
  | { readonly kind: 'CATALOG'; readonly name: string }
  | { readonly kind: 'CUSTOM'; readonly code: string; readonly version: number };

export interface GroupField {
  readonly field: string;
  readonly label?: string;
  readonly bucket?: Bucket;
}
export interface ValueField {
  readonly field: string;
  readonly aggregation: Aggregation;
  readonly label?: string;
}
export interface SortKey {
  readonly by: string;
  readonly direction: 'ASC' | 'DESC';
}
export interface CalculatedFieldDoc {
  readonly name: string;
  readonly label?: string;
  readonly expression: string;
  readonly type: FieldType;
}
export interface ChartDoc {
  readonly type: 'BAR' | 'STACKED_BAR' | 'HORIZONTAL_BAR' | 'LINE' | 'AREA' | 'PIE' | 'KPI';
  readonly legend?: 'RIGHT' | 'BOTTOM' | 'NONE';
  readonly showValues?: boolean;
  readonly yFromZero?: boolean;
  readonly donut?: boolean;
  readonly valueIndex?: number;
}

export interface ReportDocument {
  readonly schemaVersion: 1;
  readonly kind: 'report';
  readonly code?: string;
  readonly ern?: string;
  readonly name: string;
  readonly description?: string;
  readonly reportType: ReportType;
  readonly visibility: 'PRIVATE' | 'WORKSPACE';
  readonly dataset: DatasetRef;
  readonly calculatedFields?: readonly CalculatedFieldDoc[];
  readonly filters?: ReportFilters;
  readonly table?: {
    readonly columns: readonly {
      readonly field: string;
      readonly label?: string;
      readonly width?: number;
    }[];
    readonly sort?: readonly SortKey[];
    readonly limit?: number;
  };
  readonly pivot?: {
    readonly rows?: readonly GroupField[];
    readonly columns?: readonly GroupField[];
    readonly values: readonly ValueField[];
    readonly subtotals?: boolean;
    readonly sort?: readonly SortKey[];
    readonly maxCells?: number;
  };
  readonly chart?: ChartDoc;
  readonly formats?: Readonly<Record<string, ReportFormat>>;
}

export type Shelf = 'filters' | 'rows' | 'columns' | 'values';

/** P-1 and the schema's `maxItems`. */
export const LIMITS: Readonly<Record<Shelf, number>> = {
  filters: 20,
  rows: 3,
  columns: 2,
  values: 5,
};
/** A TABLE's "Spalten" shelf is its column list (≤ 60). */
export const TABLE_COLUMN_LIMIT = 60;

/** The value key of a measure (D-3): `<aggregation lowercase>:<field>`. */
export const valueKey = (v: ValueField): string => `${v.aggregation.toLowerCase()}:${v.field}`;

export function asDocument(json: Record<string, unknown> | undefined): ReportDocument | undefined {
  if (json?.kind !== 'report') return undefined;
  return json as unknown as ReportDocument;
}

/** The fields a shelf holds, in order, keyed for the token list. */
export interface ShelfEntry {
  readonly key: string;
  readonly field: string;
}

export function shelfEntries(doc: ReportDocument, shelf: Shelf): ShelfEntry[] {
  switch (shelf) {
    case 'filters':
      return (doc.filters?.clauses ?? []).map((c, i) => ({ key: String(i), field: c.field }));
    case 'rows':
      return (doc.pivot?.rows ?? []).map((r) => ({ key: r.field, field: r.field }));
    case 'columns':
      if (doc.reportType === 'TABLE')
        return (doc.table?.columns ?? []).map((c) => ({ key: c.field, field: c.field }));
      return (doc.pivot?.columns ?? []).map((c) => ({ key: c.field, field: c.field }));
    case 'values':
      return (doc.pivot?.values ?? []).map((v) => ({ key: valueKey(v), field: v.field }));
  }
}

export function shelfLimit(doc: ReportDocument, shelf: Shelf): number {
  return doc.reportType === 'TABLE' && shelf === 'columns' ? TABLE_COLUMN_LIMIT : LIMITS[shelf];
}

/** Which shelves a report type shows: a table has Filter and Spalten (its columns). */
export function shelvesOf(type: ReportType): readonly Shelf[] {
  return type === 'TABLE' ? ['filters', 'columns'] : ['filters', 'rows', 'columns', 'values'];
}

const pivotOf = (doc: ReportDocument): NonNullable<ReportDocument['pivot']> =>
  doc.pivot ?? { rows: [], columns: [], values: [], subtotals: true };

function defaultAggregation(type: FieldType | undefined): Aggregation {
  return type === 'NUMBER' ? 'SUM' : 'COUNT';
}

export type MoveResult =
  | {
      readonly ok: true;
      readonly doc: ReportDocument;
      readonly position: number;
      readonly count: number;
    }
  | { readonly ok: false; readonly reason: 'full' | 'filter' | 'duplicate' | 'notAllowed' };

/**
 * Puts `field` on `shelf` (U-2). A filter needs an operator and a value, so a move onto Filter
 * answers `filter` and the editor opens the operator picker (U-3). A full shelf refuses (P-1).
 */
export function addField(
  doc: ReportDocument,
  field: string,
  shelf: Shelf,
  type: FieldType | undefined,
): MoveResult {
  if (shelf === 'filters') return { ok: false, reason: 'filter' };
  if (!shelvesOf(doc.reportType).includes(shelf)) return { ok: false, reason: 'notAllowed' };
  const entries = shelfEntries(doc, shelf);
  if (entries.length >= shelfLimit(doc, shelf)) return { ok: false, reason: 'full' };
  if (doc.reportType === 'TABLE') {
    if (entries.some((e) => e.field === field)) return { ok: false, reason: 'duplicate' };
    const columns = [...(doc.table?.columns ?? []), { field }];
    return {
      ok: true,
      doc: { ...doc, table: { ...(doc.table ?? {}), columns } },
      position: columns.length,
      count: columns.length,
    };
  }
  const pivot = pivotOf(doc);
  if (shelf === 'values') {
    const added: ValueField = { field, aggregation: defaultAggregation(type) };
    const values = [...pivot.values, added];
    if (pivot.values.some((v) => valueKey(v) === valueKey(added))) {
      return { ok: false, reason: 'duplicate' };
    }
    return {
      ok: true,
      doc: { ...doc, pivot: { ...pivot, values } },
      position: values.length,
      count: values.length,
    };
  }
  // A field is on at most one axis: moving it to rows takes it off columns and vice versa.
  const rows = (pivot.rows ?? []).filter((r) => r.field !== field);
  const columns = (pivot.columns ?? []).filter((c) => c.field !== field);
  const target = shelf === 'rows' ? rows : columns;
  if (target.length >= LIMITS[shelf]) return { ok: false, reason: 'full' };
  const next = [...target, { field }];
  const updated =
    shelf === 'rows' ? { ...pivot, rows: next, columns } : { ...pivot, rows, columns: next };
  return { ok: true, doc: { ...doc, pivot: updated }, position: next.length, count: next.length };
}

/** Takes a token off its shelf. */
export function removeToken(doc: ReportDocument, shelf: Shelf, key: string): ReportDocument {
  switch (shelf) {
    case 'filters': {
      const clauses = (doc.filters?.clauses ?? []).filter((_, i) => String(i) !== key);
      return { ...doc, filters: { join: doc.filters?.join ?? 'ALL', clauses } };
    }
    case 'rows':
      return {
        ...doc,
        pivot: { ...pivotOf(doc), rows: (doc.pivot?.rows ?? []).filter((r) => r.field !== key) },
      };
    case 'columns':
      if (doc.reportType === 'TABLE') {
        return {
          ...doc,
          table: {
            ...(doc.table ?? { columns: [] }),
            columns: (doc.table?.columns ?? []).filter((c) => c.field !== key),
          },
        };
      }
      return {
        ...doc,
        pivot: {
          ...pivotOf(doc),
          columns: (doc.pivot?.columns ?? []).filter((c) => c.field !== key),
        },
      };
    case 'values':
      return {
        ...doc,
        pivot: {
          ...pivotOf(doc),
          values: (doc.pivot?.values ?? []).filter((v) => valueKey(v) !== key),
        },
      };
  }
}

/** Moves a token from one shelf to another; Filter answers `filter` (the picker decides the clause). */
export function moveToken(
  doc: ReportDocument,
  from: Shelf,
  key: string,
  to: Shelf,
  type: FieldType | undefined,
): MoveResult {
  const entry = shelfEntries(doc, from).find((e) => e.key === key);
  if (entry === undefined) return { ok: false, reason: 'notAllowed' };
  if (to === 'filters') return { ok: false, reason: 'filter' };
  const without = removeToken(doc, from, key);
  return addField(without, entry.field, to, type);
}

/** Alt+↑ / Alt+↓ and the drag within a shelf (U-2). */
export function reorder(
  doc: ReportDocument,
  shelf: Shelf,
  from: number,
  to: number,
): ReportDocument {
  const move = <T>(list: readonly T[]): T[] => {
    const next = [...list];
    const [item] = next.splice(from, 1);
    if (item !== undefined) next.splice(to, 0, item);
    return next;
  };
  switch (shelf) {
    case 'filters':
      return {
        ...doc,
        filters: { join: doc.filters?.join ?? 'ALL', clauses: move(doc.filters?.clauses ?? []) },
      };
    case 'rows':
      return { ...doc, pivot: { ...pivotOf(doc), rows: move(doc.pivot?.rows ?? []) } };
    case 'columns':
      if (doc.reportType === 'TABLE')
        return {
          ...doc,
          table: { ...(doc.table ?? { columns: [] }), columns: move(doc.table?.columns ?? []) },
        };
      return { ...doc, pivot: { ...pivotOf(doc), columns: move(doc.pivot?.columns ?? []) } };
    case 'values':
      return { ...doc, pivot: { ...pivotOf(doc), values: move(doc.pivot?.values ?? []) } };
  }
}

export function addClause(doc: ReportDocument, clause: ReportFilterClause): ReportDocument {
  return {
    ...doc,
    filters: {
      join: doc.filters?.join ?? 'ALL',
      clauses: [...(doc.filters?.clauses ?? []), clause],
    },
  };
}

/** The format key of a token: the field, or the value key (D-3). */
export function formatKeyOf(shelf: Shelf, key: string, doc: ReportDocument): string {
  if (shelf === 'values') return key;
  return shelfEntries(doc, shelf).find((e) => e.key === key)?.field ?? key;
}

/**
 * Sets a title override and a format (D-4: one place — the token's `label`; formats by key). An
 * undefined format removes the override (back to the workspace default).
 */
export function setPresentation(
  doc: ReportDocument,
  shelf: Shelf,
  key: string,
  title: string | undefined,
  format: ReportFormat | undefined,
): ReportDocument {
  const formatKey = formatKeyOf(shelf, key, doc);
  const formats: Record<string, ReportFormat> = Object.fromEntries(
    Object.entries(doc.formats ?? {}).filter(([k]) => k !== formatKey),
  );
  if (format !== undefined) formats[formatKey] = format;
  const label = <T extends { readonly label?: string }>(item: T): T => {
    const { label: _old, ...rest } = item;
    return (title === undefined || title === '' ? rest : { ...rest, label: title }) as T;
  };
  let next: ReportDocument = { ...doc, formats };
  switch (shelf) {
    case 'rows':
      next = {
        ...next,
        pivot: {
          ...pivotOf(next),
          rows: (next.pivot?.rows ?? []).map((r) => (r.field === key ? label(r) : r)),
        },
      };
      break;
    case 'columns':
      if (next.reportType === 'TABLE') {
        next = {
          ...next,
          table: {
            ...(next.table ?? { columns: [] }),
            columns: (next.table?.columns ?? []).map((c) => (c.field === key ? label(c) : c)),
          },
        };
      } else {
        next = {
          ...next,
          pivot: {
            ...pivotOf(next),
            columns: (next.pivot?.columns ?? []).map((c) => (c.field === key ? label(c) : c)),
          },
        };
      }
      break;
    case 'values':
      next = {
        ...next,
        pivot: {
          ...pivotOf(next),
          values: (next.pivot?.values ?? []).map((v) => (valueKey(v) === key ? label(v) : v)),
        },
      };
      break;
    case 'filters':
      next = {
        ...next,
        filters: {
          join: next.filters?.join ?? 'ALL',
          clauses: (next.filters?.clauses ?? []).map((c, i) => (String(i) === key ? label(c) : c)),
        },
      };
      break;
  }
  return next;
}

/** Changes a value's aggregation; its format moves to the new value key. */
export function setAggregation(
  doc: ReportDocument,
  key: string,
  aggregation: Aggregation,
): ReportDocument {
  const old = doc.formats?.[key];
  let renamed = key;
  const values = (doc.pivot?.values ?? []).map((v) => {
    if (valueKey(v) !== key) return v;
    const next = { ...v, aggregation };
    renamed = valueKey(next);
    return next;
  });
  const formats: Record<string, ReportFormat> = Object.fromEntries(
    Object.entries(doc.formats ?? {}).filter(([k]) => k !== key),
  );
  if (old !== undefined) formats[renamed] = old;
  return { ...doc, formats, pivot: { ...pivotOf(doc), values } };
}

export function setBucket(
  doc: ReportDocument,
  shelf: 'rows' | 'columns',
  field: string,
  bucket: Bucket | undefined,
): ReportDocument {
  const apply = (g: GroupField): GroupField => {
    if (g.field !== field) return g;
    const { bucket: _old, ...rest } = g;
    return bucket === undefined ? rest : { ...rest, bucket };
  };
  const pivot = pivotOf(doc);
  return {
    ...doc,
    pivot:
      shelf === 'rows'
        ? { ...pivot, rows: (pivot.rows ?? []).map(apply) }
        : { ...pivot, columns: (pivot.columns ?? []).map(apply) },
  };
}

/** Switches Tabelle / Pivot-Tabelle / Diagramm, keeping what carries over (a chart is a drawn pivot). */
export function setReportType(doc: ReportDocument, type: ReportType): ReportDocument {
  if (type === doc.reportType) return doc;
  if (type === 'TABLE') {
    const fields = [...(doc.pivot?.rows ?? []), ...(doc.pivot?.columns ?? [])].map((g) => ({
      field: g.field,
    }));
    const { pivot: _p, chart: _c, ...rest } = doc;
    return {
      ...rest,
      reportType: 'TABLE',
      table: { columns: doc.table?.columns ?? fields, limit: 10000 },
    };
  }
  const { table, ...rest } = doc;
  const pivot = doc.pivot ?? {
    rows: (table?.columns ?? []).slice(0, 3).map((c) => ({ field: c.field })),
    columns: [],
    values: [],
    subtotals: true,
  };
  if (type === 'CHART')
    return {
      ...rest,
      reportType: 'CHART',
      pivot,
      chart: doc.chart ?? { type: 'BAR', legend: 'RIGHT', yFromZero: true },
    };
  const { chart: _c, ...withoutChart } = rest;
  return { ...withoutChart, reportType: 'PIVOT', pivot };
}

/** A new, empty report on a dataset (the list's "Neuer Bericht" and "Bericht mit diesem Datensatz"). */
export function newReport(
  name: string,
  dataset: DatasetRef,
  type: ReportType = 'PIVOT',
  /** The dataset's first field: the schema wants one value (pivot, chart) or one column (table). */
  firstField?: string,
): ReportDocument {
  const values =
    firstField === undefined ? [] : [{ field: firstField, aggregation: 'COUNT' as const }];
  const columns = firstField === undefined ? [] : [{ field: firstField }];
  const base = {
    schemaVersion: 1 as const,
    kind: 'report' as const,
    name,
    visibility: 'PRIVATE' as const,
    dataset,
    filters: { join: 'ALL' as const, clauses: [] },
  };
  if (type === 'TABLE') return { ...base, reportType: 'TABLE', table: { columns, limit: 10000 } };
  if (type === 'CHART')
    return {
      ...base,
      reportType: 'CHART',
      pivot: { rows: [], columns: [], values, subtotals: true },
      chart: { type: 'BAR', legend: 'RIGHT', yFromZero: true },
    };
  return {
    ...base,
    reportType: 'PIVOT',
    pivot: { rows: [], columns: [], values, subtotals: true },
  };
}

/** Every field name the document uses — for the stale-field check against the catalog (board 10). */
export function usedFields(doc: ReportDocument): string[] {
  const names = [
    ...(doc.filters?.clauses ?? []).map((c) => c.field),
    ...(doc.table?.columns ?? []).map((c) => c.field),
    ...(doc.pivot?.rows ?? []).map((r) => r.field),
    ...(doc.pivot?.columns ?? []).map((c) => c.field),
    ...(doc.pivot?.values ?? []).map((v) => v.field),
  ];
  return [...new Set(names)];
}

/** A shelf-ready document for the preview: nothing to run without a value or a column (U-1). */
export function isRunnable(doc: ReportDocument): boolean {
  if (doc.reportType === 'TABLE') return (doc.table?.columns.length ?? 0) > 0;
  return (doc.pivot?.values.length ?? 0) > 0;
}
